import { SearchModality } from '@aibees/elephant/feature/search-table-company/hooks/types';
import {
  ExportSearchGrouping,
  useAddExportMutation,
  useSearchModality,
  useSearchSimilarityFromRoute,
  useSearchTypeFromRoute,
  useTrackEvents,
} from '@aibees/elephant/hooks';
import { usePageType } from '@aibees/elephant/hooks/use-page-type';
import { useSaveRecord } from '@aibees/elephant/hooks/use-save-record';
import { MixpanelEvent, SearchMode, SearchType } from '@aibees/elephant/types';
import { IntegrationConfig } from '@aibees/elephant/types/integration';
import { Label, LabelType } from '@aibees/elephant/types/label';
import { notificationIconMap } from '@aibees/elephant/utils';
import { useAuthContext } from '@aibees/shared/auth';
import { FormDialogOnSubmitStatus } from '@aibees/shared/components';
import {
  ErrorResponse,
  Nullable,
  PresetVisibility,
} from '@aibees/shared/definition';
import { useBoolean, useNotify } from '@aibees/shared/hooks';
import { useInsufficientCreditStore } from '@aibees/shared/store';
import { toUrlQueryString } from '@aibees/shared/utils';
import { useWorkspace, useWorkspaceUsers } from '@aibees/shared/workspace';
import { random } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useActionButtonContext } from '../action-button-context';
import { Provider } from './context';
import {
  TableActionContextProps as ContextProps,
  TableActionContextData,
} from './types';
import { getSaveRecordsActionsFunction } from './utils';

type MutationHandlerConfig = {
  mutationFn: () => Promise<any>;
  successMessage: string;
  progressMessage: string;
  completeMessage: string;
  completeDescription?: string;
  icon: any;
  trackEvent?: {
    name: MixpanelEvent;
    payload: Record<string, any>;
  };
  clearLists?: boolean;
  e?: FormDialogOnSubmitStatus;
  clearSelectedOwner?: boolean;
};

export const TableActionProvider = ({ children, value }: ContextProps) => {
  const { modality } = useSearchModality();

  // State management
  const { item, setItem } = useActionButtonContext();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [lists, setLists] = useState<Label[]>([]);
  const [labels, setLabels] = useState<Label[]>([]);
  const [selectedItems, setSelectedItems] = useState<Label[]>([]);
  const [selectedOwner, setSelectedOwner] = useState<string>('');
  const [previousState, setPreviousState] = useState<string>('');
  const [exportName, setExportName] = useState<string>('');
  const [tabValue, setTabValue] = useState<PresetVisibility>(
    PresetVisibility.ALL
  );
  const [integrationConfig, setIntegrationConfig] =
    useState<IntegrationConfig>();
  // Hooks
  const notify = useNotify();
  const { track } = useTrackEvents();
  const workspace = useWorkspace();
  const { user } = useAuthContext();
  const users = useWorkspaceUsers(true);
  const searchType = useSearchTypeFromRoute();
  const isSimilarity = useSearchSimilarityFromRoute();
  const { isPeoplePage } = usePageType();
  const { setIsError } = useInsufficientCreditStore();

  const effectiveSearchType = useMemo(() => {
    return (
      searchType ?? (isPeoplePage ? SearchType.People : SearchType.Company)
    );
  }, [searchType, isPeoplePage]);

  const selectedUser = useMemo(() => {
    return users.find((u) => u.id === selectedOwner);
  }, [selectedOwner, users]);

  // API hooks
  const api = useSaveRecord(effectiveSearchType);
  const exportApi = useAddExportMutation();

  // Memoized derived values
  const ids = useMemo(
    () => (item?.id ? [item.id] : selectedIds),
    [item, selectedIds]
  );

  const getSaveRecordsActions = useCallback(
    (data: any) =>
      getSaveRecordsActionsFunction(
        data,
        effectiveSearchType,
        modality as SearchModality
      ),
    [effectiveSearchType, modality]
  );

  // Dialog states grouped together
  const save = useBoolean();
  const exportState = useBoolean(false);
  const list = useBoolean();
  const label = useBoolean();
  const owner = useBoolean();
  const removeLabel = useBoolean();
  const removeList = useBoolean();
  const createExportTemplate = useBoolean();
  const createLabel = useBoolean();
  const createList = useBoolean();
  const template = useBoolean();
  const integrationConnect = useBoolean();

  const dialogStates = useMemo(
    () => ({
      save,
      export: exportState,
      list,
      label,
      owner,
      removeLabel,
      removeList,
      createExportTemplate,
      createLabel,
      createList,
      template,
      integrationConnect,
    }),
    [
      save,
      exportState,
      list,
      label,
      owner,
      removeLabel,
      removeList,
      createExportTemplate,
      createLabel,
      createList,
      template,
      integrationConnect,
    ]
  );

  // Generic mutation handler
  const handleMutation = useCallback(
    async ({
      mutationFn,
      successMessage,
      progressMessage,
      completeMessage,
      completeDescription,
      icon,
      trackEvent,
      clearLists = false,
      e,
      clearSelectedOwner = false,
    }: MutationHandlerConfig) => {
      const key = Date.now().toString();

      // Error handler
      const handleError = (error: Nullable<ErrorResponse>) => {
        if (error?.cause?.code === 402) {
          notify.closeSnackbar(key);
          setIsError(true);
        }
      };

      // Start progress notification
      notify.progress(
        progressMessage,
        `Processing <b>${ids.length}</b> records...`,
        key,
        { icon }
      );

      try {
        await mutationFn();

        // Update to success notification
        notify.closeSnackbar(key);
        notify.progress(
          successMessage,
          `Processing <b>${ids.length}</b> records...`,
          random(1, 100),
          { icon }
        );

        // Final completion notification
        notify.complete(
          completeMessage,
          completeDescription ||
            `Completed processing <b>${ids.length}</b> records.`,
          random(1, 101),
          { icon }
        );

        // Track event if provided
        if (trackEvent) {
          track(trackEvent.name, trackEvent.payload);
        }
      } catch (error) {
        handleError(error);
        e?.toggle();
      } finally {
        e?.toggle();
        // notify.closeSnackbar(key);
        if (clearLists) {
          setLists([]);
          setLabels([]);
        }
      }
    },
    [notify, ids.length, setIsError, track]
  );

  // Action handlers
  const handleSave = useCallback<TableActionContextData['handleSave']>(
    (data, e) => {
      handleMutation({
        mutationFn: () =>
          api.mutateAsync({
            ids,
            kind: 'ADD',
            user: selectedUser,
            labels: [...lists, ...labels].map((x) => x.id),
            actions: getSaveRecordsActions(data)(),
            overwriteOwner: data.overwriteOwner,
          }),
        successMessage: 'Saving Records in Progress',
        progressMessage: 'Save Records is in progress',
        completeMessage: 'Your Save Records process is complete',
        icon: notificationIconMap.SaveRecords,
        clearSelectedOwner: true,
      });

      dialogStates.save.onFalse();
    },
    [
      handleMutation,
      dialogStates.save,
      api,
      ids,
      selectedUser,
      lists,
      labels,
      getSaveRecordsActions,
    ]
  );

  const handleExport = useCallback<TableActionContextData['handleExport']>(
    (data, e, params, setting, integrations) => {
      if (!ids.length) return;

      const metadata = { url: '?'.concat(toUrlQueryString(params)) };

      handleMutation({
        mutationFn: () =>
          exportApi.mutateAsync({
            name: exportName,
            grouping:
              effectiveSearchType === SearchType.People
                ? ExportSearchGrouping.PERSON
                : ExportSearchGrouping.COMPANY,
            ids,
            user: selectedUser,
            overwriteOwner: data.overwriteOwner,
            actions: getSaveRecordsActions(data)(),
            labels: [...labels, ...lists].map((x) => x.id),
            type: isSimilarity
              ? SearchMode.Similarity.toUpperCase()
              : SearchMode.Semantic.toUpperCase(),
            metadata,
            setting,
            integrations,
          }),
        successMessage: 'Export file is in progress',
        progressMessage: 'Export is in progress',
        completeMessage: 'Your export process is complete',
        icon: notificationIconMap.EXPORT,
        trackEvent: {
          name: 'EXPORT_RECORDS',
          payload: {
            searchType: effectiveSearchType,
            multiple: ids.length > 1,
          },
        },
        clearLists: true,
        e,
        clearSelectedOwner: true,
      });

      dialogStates.export.onFalse();
      setExportName('');
    },
    [
      ids,
      handleMutation,
      effectiveSearchType,
      dialogStates.export,
      exportApi,
      exportName,
      selectedUser,
      getSaveRecordsActions,
      labels,
      lists,
      isSimilarity,
    ]
  );

  const handleAddToList = useCallback<
    TableActionContextData['handleAddToLabel']
  >(
    (data, e) => {
      handleMutation({
        mutationFn: () =>
          api.mutateAsync({
            ids,
            kind: 'ADD',
            labels: lists.map((x) => x.id) || [],
            overwriteOwner: data.overwriteOwner,
            user: selectedUser,
            actions: getSaveRecordsActions(data)(),
          }),
        successMessage: 'Adding Records to list is in progress',
        progressMessage: 'Add to list is in progress',
        completeMessage: 'Adding Records to list is complete',
        icon: notificationIconMap.List,
        trackEvent: {
          name: 'ADD_LIST_TO_RECORDS',
          payload: {
            searchType: effectiveSearchType,
            multiple: ids.length > 1,
            component: 'Add List ChangePlanDialog',
          },
        },
        clearLists: true,
        clearSelectedOwner: true,
      });

      handleClose('list');
    },
    [
      users,
      handleMutation,
      effectiveSearchType,
      ids,
      dialogStates.list,
      user?.id,
      api,
      lists,
      getSaveRecordsActions,
    ]
  );

  const handleAddToLabel = useCallback<
    TableActionContextData['handleAddToLabel']
  >(
    (data, e) => {
      handleMutation({
        mutationFn: () =>
          api.mutateAsync({
            ids,
            kind: 'ADD',
            labels: labels.map((x) => x.id) || [],
            overwriteOwner: data.overwriteOwner,
            user: selectedUser,
            actions: getSaveRecordsActions(data)(),
          }),
        successMessage: 'Adding Records to Label is in progress',
        progressMessage: 'Add to Label is in progress',
        completeMessage: 'Adding Records to Label is complete',
        icon: notificationIconMap.Label,
        trackEvent: {
          name: 'ADD_LABEL_TO_RECORDS',
          payload: {
            searchType: effectiveSearchType,
            multiple: ids.length > 1,
            component: 'Add Label ChangePlanDialog',
          },
        },
        clearSelectedOwner: true,
      });

      handleClose('label');
    },
    [
      users,
      handleMutation,
      effectiveSearchType,
      ids,
      dialogStates.label,
      user?.id,
      api,
      labels,
      getSaveRecordsActions,
    ]
  );

  const handleAssignOwner = useCallback<
    TableActionContextData['handleAssignOwner']
  >(
    async (data, e) => {
      await handleMutation({
        mutationFn: () =>
          api.mutateAsync({
            ids,
            kind: 'ADD',
            user: selectedUser,
            labels: [...labels, ...lists].map((x) => x.id),
            overwriteOwner: data.overwriteOwner,
            actions: getSaveRecordsActions(data)(),
          }),
        successMessage: 'Assign owner in Progress',
        progressMessage: 'Assign owner is in progress',
        completeMessage: 'Assign owner success',
        icon: notificationIconMap.AssignOwner,
        clearSelectedOwner: true,
      });

      dialogStates.owner.onFalse();
    },
    [
      users,
      handleMutation,
      dialogStates.owner,
      selectedOwner,
      api,
      ids,
      labels,
      lists,
      getSaveRecordsActions,
    ]
  );

  const handleRemoveLabel = useCallback(
    (labelType: LabelType) => (selected: Label[]) => {
      const isSingleLabel = selected.length === 1;
      const isSingleItem = ids.length === 1;
      const type =
        labelType === LabelType.LABEL
          ? isSingleLabel
            ? 'Label'
            : 'Labels'
          : isSingleLabel
          ? 'List'
          : 'Lists';
      const itemText = isSingleItem ? 'Item' : 'Items';
      const icon =
        labelType === LabelType.LABEL
          ? notificationIconMap.RemoveLabel
          : notificationIconMap.RemoveList;

      handleMutation({
        mutationFn: () =>
          api.mutateAsync({
            ids,
            kind: 'REMOVE',
            labels: selected.map((x) => x.id),
            user: workspace?.user,
          }),
        successMessage: `Removing from ${type} is in progress`,
        progressMessage: `Removing from ${type} is in progress`,
        completeMessage: `Remove from ${type}`,
        completeDescription: `${itemText} were successfully removed from your selected ${type}.`,
        icon,
      });

      if (labelType === LabelType.LABEL) {
        handleClose('removeLabel');
      } else {
        handleClose('removeList');
      }
    },
    [api, handleMutation, ids, workspace?.user]
  );

  const handleClose = useCallback(
    (type: keyof typeof dialogStates) => {
      dialogStates[type].onFalse();
      // Only clear the item, but preserve labels/lists data
      setItem(undefined);
      setLists([]);
      setLabels([]);
      setSelectedOwner('');
      setTabValue(PresetVisibility.ALL);
      // Don't clear labels/lists here as they should persist across accordion open/close
    },
    [dialogStates, setItem]
  );

  // Memoized provider value
  const providerValue = useMemo(
    () => ({
      // Dialog states
      saveDialogState: dialogStates.save,
      exportDialogState: dialogStates.export,
      listDialogState: dialogStates.list,
      labelDialogState: dialogStates.label,
      ownerDialogState: dialogStates.owner,
      removeLabelDialogState: dialogStates.removeLabel,
      removeListDialogState: dialogStates.removeList,
      createExportTemplateDialogState: dialogStates.createExportTemplate,
      createLabelDialogState: dialogStates.createLabel,
      createListDialogState: dialogStates.createList,
      templateDialogState: dialogStates.template,
      integrationConnectDialogState: dialogStates.integrationConnect,
      previousState,
      // State values
      lists,
      labels,
      selectedItems,
      ids,
      selectedOwner,
      exportName,
      integrationConfig,
      tabValue,
      // State setters
      setLists,
      setLabels,
      setSelectedItems,
      setSelectedOwner,
      setIds: setSelectedIds,
      setPreviousState,
      setExportName,
      setIntegrationConfig,
      setTabValue,
      // Handlers
      handleSave,
      handleExport,
      handleAddToList,
      handleAddToLabel,
      handleAssignOwner,
      handleRemoveLabel,
      // Unified close handler
      handleClose,
    }),
    [
      dialogStates.save,
      dialogStates.export,
      dialogStates.list,
      dialogStates.label,
      dialogStates.owner,
      dialogStates.removeLabel,
      dialogStates.removeList,
      dialogStates.createExportTemplate,
      dialogStates.createLabel,
      dialogStates.createList,
      dialogStates.template,
      dialogStates.integrationConnect,
      previousState,
      lists,
      labels,
      selectedItems,
      ids,
      selectedOwner,
      integrationConfig,
      handleSave,
      handleExport,
      handleAddToList,
      handleAddToLabel,
      handleAssignOwner,
      handleRemoveLabel,
      handleClose,
    ]
  );

  return <Provider value={providerValue}>{children}</Provider>;
};
